import * as React from 'react';
import { useEffect, useState } from 'react';
import { fetchAvailableDiscounts } from '../loaders';

/*
	Shared display for the automatic goodwill-gesture discount, surfaced as early as we
	can: a promo pill on the plan cards and a banner on the checkout steps, not just at
	payment. The data comes from GET /resa/discounts (catalogue-level, before a sale
	exists) or GET /resa/discount (the one applied to the sale, at payment).

	All wording is the club's own — the gesture's publicDescription (preferred, it is the
	field meant for customers) or its name. Nothing is composed here, so nothing is
	hard-coded: change the copy in Resamania and it changes here.
*/

export type Discount = Readonly<{
	name?: string,
	description?: string,
	publicDescription?: string,
	appliesToSubscription?: boolean,
	appliesToOneOff?: boolean,
	// The gesture's own percentages, so the summary can compute the discounted price
	// before a sale exists. prorata = the pro-rata part of the first payment; repayment =
	// the whole month(s) paid in advance; registrationFee = the joining fee.
	prorataDiscountPercent?: number,
	registrationFeeDiscountPercent?: number,
	repaymentDiscountPercent?: number,
}>;

/* What useSummaryCalculator returns — the pieces a discount is applied to. */
type SummaryParts = {
	amountTotal: number,
	membershipDueToday: number,
	monthlyFee: number,
	adminFee: number,
	firstMonthPrepaid: boolean,
};

/*
	Apply a discount's percentages to the summary to get the discounted amount due today
	and the saving. Mirrors Resamania's own split: the first payment is the current-month
	pro-rata plus, for a +15 offer, one whole month paid in advance; the gesture discounts
	the pro-rata, the advance month(s) and the joining fee independently. Verified against
	a live sale to the penny (Off-Peak +15: £103.26 → £64.95). Returns the undiscounted
	total unchanged when there is no discount.
*/
export function applyDiscount(summary: SummaryParts, discount: Discount | null | undefined): { discountedTotal: number, saving: number, hasDiscount: boolean } {
	const prorataOff = (discount?.prorataDiscountPercent ?? 0) / 100;
	const repaymentOff = (discount?.repaymentDiscountPercent ?? 0) / 100;
	const registrationOff = (discount?.registrationFeeDiscountPercent ?? 0) / 100;

	if (prorataOff === 0 && repaymentOff === 0 && registrationOff === 0) {
		return { discountedTotal: summary.amountTotal, saving: 0, hasDiscount: false };
	}

	const advanceMonth = summary.firstMonthPrepaid ? summary.monthlyFee : 0;
	const proRataPart = summary.membershipDueToday - advanceMonth;

	const discountedTotal =
		proRataPart * (1 - prorataOff) +
		advanceMonth * (1 - repaymentOff) +
		summary.adminFee * (1 - registrationOff);

	const saving = summary.amountTotal - discountedTotal;

	return { discountedTotal, saving, hasDiscount: saving > 0.005 };
}

/*
	The saving broken into the parts the gesture discounts, so the summary can show *what*
	is being saved (e.g. "Rest of September free −£5.00", "50% off joining fee −£15.00")
	instead of one unexplained lump. Labels are composed from the gesture's own percentages
	and the start month, so they track whatever the club is running (a 50% pro-rata month
	reads "50% off the rest of October", a fee-only month drops the pro-rata line, etc.) —
	no promo wording is hard-coded.

	`totalDiscount` is the reconciled saving the summary shows (struck − amount due). We work
	in whole pennies and give the *pro-rata* line the remainder, so the lines always sum to
	exactly that saving. The pro-rata is a fractional day rate (e.g. 3/30 of a £49.95 month =
	£4.995) computed via a subtraction that drifts a half-penny, which is precisely where the
	rounding must land — so absorbing it there keeps the whole summary adding up.
*/
export function discountLines(summary: SummaryParts, discount: Discount | null | undefined, startDate: Date, totalDiscount: number): { label: string, amount: number }[] {
	const prorataPercent = discount?.prorataDiscountPercent ?? 0;
	const repaymentPercent = discount?.repaymentDiscountPercent ?? 0;
	const registrationPercent = discount?.registrationFeeDiscountPercent ?? 0;

	const advanceMonth = summary.firstMonthPrepaid ? summary.monthlyFee : 0;
	const monthName = startDate.toLocaleDateString('en-GB', { month: 'long' });

	// Whole pennies. registration and advance are exact percentages of whole amounts; the
	// pro-rata line takes whatever is left so the three always sum to the shown saving.
	const totalPence = Math.round(totalDiscount * 100);
	const registrationPence = Math.round(summary.adminFee * registrationPercent);   // adminFee × (pct/100) × 100
	const advancePence = Math.round(advanceMonth * repaymentPercent);
	const prorataPence = totalPence - registrationPence - advancePence;

	const lines: { label: string, amount: number }[] = [];

	if (prorataPercent > 0 && prorataPence > 0) {
		lines.push({ label: prorataPercent >= 100 ? `Rest of ${monthName} free` : `${prorataPercent}% off the rest of ${monthName}`, amount: prorataPence / 100 });
	}

	if (repaymentPercent > 0 && advancePence > 0) {
		lines.push({ label: repaymentPercent >= 100 ? 'Next month free' : `${repaymentPercent}% off next month`, amount: advancePence / 100 });
	}

	if (registrationPercent > 0 && registrationPence > 0) {
		lines.push({ label: registrationPercent >= 100 ? 'No joining fee' : `${registrationPercent}% off joining fee`, amount: registrationPence / 100 });
	}

	return lines;
}

/* The reconciled saving to show: struck (undiscounted) minus the amount due, both rounded
   to pennies the same way the display does, so struck − saving == amount due exactly. */
export function reconciledSaving(undiscounted: number, amountDue: number): number {
	return (Math.round(undiscounted * 100) - Math.round(amountDue * 100)) / 100;
}

/* The club's headline text for the discount: publicDescription if set, else the name. */
export function discountText(discount: Discount): string {
	const publicDescription = (discount.publicDescription ?? '').trim();
	return publicDescription !== '' ? publicDescription : (discount.name ?? '').trim();
}

/*
	The discount that applies to an offer of the given kind, or null. A plan card / step
	is for one offer, so it shows the discount matching that offer's type (subscription
	vs one-off) — the same rule the API uses to decide eligibility per sale.
*/
export function discountForOffer(discounts: Discount[] | null | undefined, subscription: boolean): Discount | null {
	if (!Array.isArray(discounts)) {
		return null;
	}
	return discounts.find((d) => (subscription ? d.appliesToSubscription : d.appliesToOneOff)) ?? null;
}

/*
	A single checkout step is for one chosen offer, so this fetches the club's current
	discounts and hands back the one that applies to that offer (or null). Lets each step
	surface the discount with one line, without repeating the fetch.
*/
export function useAvailableDiscount(hash: string, subscription: boolean): Discount | null {
	const [discounts, setDiscounts] = useState<Discount[]>([]);

	useEffect(() => {
		if (!hash) {
			return;
		}
		fetchAvailableDiscounts(hash).then((data) => setDiscounts(Array.isArray(data) ? data : []));
	}, [hash]);

	return discountForOffer(discounts, subscription);
}

/* Full-width promo pill across the top of a plan card, showing the club's own wording. */
export function DiscountPill({ discount, className = '' }: { discount: Discount, className?: string }) {
	const text = discountText(discount);
	if (text === '') {
		return null;
	}

	return (
		<div className={"w-full rounded-[1.25rem] bg-[#f5e6a6] text-black text-center font-bold uppercase tracking-wide text-sm px-5 py-3 " + className}>
			{text}
		</div>
	);
}

/*
	The discount as its own cream card for a checkout step: a small label over the club's
	own offer wording. Rounded like the other cards so it sits as a distinct card above
	the step content, and it shows the wording once (no emoji, no repeated description) —
	the club's copy is used as-is so they need not change how they run the system.
*/
export function DiscountBanner({ discount, className = '' }: { discount: Discount, className?: string }) {
	const text = discountText(discount);
	if (text === '') {
		return null;
	}

	return (
		<div className={"bg-[#f5e6a6] text-black rounded-3xl p-6 sm:p-8 text-center " + className}>
			<p className="text-xs font-bold uppercase tracking-wider text-black/60 mb-3">You’re getting a discount</p>
			<p className="text-lg sm:text-2xl font-black leading-tight mb-0">{text}</p>
		</div>
	);
}
